import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Trophy, ShieldCheck, AlertTriangle, ShieldAlert, ChevronRight, Package } from "lucide-react";
import { GRADE_STYLE } from "./SupplierScorecardChip";

const GRADE_ORDER = ["A", "B", "C", "D"];

const formatCurrency = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

/**
 * Banner que aparece arriba en SuppliersPage cuando hay datos de scorecard
 * (al menos 1 proveedor con pedidos recibidos en los últimos 90d). Muestra
 * KPIs agregados y permite abrir un diálogo con el ranking detallado.
 */
export default function SupplierScorecardBanner({ data }) {
  const [open, setOpen] = useState(false);
  if (!data || !data.summary || (data.items || []).length === 0) return null;

  const { summary, items, days } = data;
  const top = items[0]; // Best graded supplier
  const worst = [...items].reverse().find((s) => s.grade === "D" || s.grade === "C");
  const ratesByGrade = items.reduce((acc, it) => {
    acc[it.grade] = (acc[it.grade] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <div
        className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-3 flex items-center gap-3"
        data-testid="supplier-scorecard-banner"
      >
        <div className="shrink-0 h-9 w-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-medium text-indigo-900">
            Scorecard de proveedores ({days}d):{" "}
            <span className="font-semibold">{summary.suppliers_count}</span> evaluados ·{" "}
            <span className="font-semibold">{summary.total_orders}</span> pedidos ·{" "}
            <span className="font-semibold">{summary.total_incidents}</span> incidencias
          </div>
          <div className="text-xs text-indigo-700/80 truncate">
            {top && (
              <span>
                Mejor: <strong>{top.supplier_name}</strong> ({top.grade})
              </span>
            )}
            {worst && (
              <span>
                {top ? " · " : ""}A vigilar: <strong>{worst.supplier_name}</strong> ({worst.grade})
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 flex-shrink-0"
          data-testid="supplier-scorecard-open-btn"
        >
          Ver ranking <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="supplier-scorecard-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              Scorecard de proveedores · últimos {days} días
            </DialogTitle>
            <DialogDescription>
              Calculado a partir de pedidos recibidos: completitud (qty recibida vs pedida) e
              incidencias (faltantes, dañados, productos incorrectos). Score = 60% completitud + 40% (1 − tasa de incidencia).
            </DialogDescription>
          </DialogHeader>

          {/* Grade distribution */}
          <div className="grid grid-cols-4 gap-2 text-center">
            {GRADE_ORDER.map((g) => {
              const style = GRADE_STYLE[g];
              const n = ratesByGrade[g] || 0;
              return (
                <div
                  key={g}
                  className={`rounded-lg border px-3 py-2 ${style.badge}`}
                  data-testid={`scorecard-grade-count-${g}`}
                >
                  <div className="text-2xl font-bold">{n}</div>
                  <div className="text-xs">Nota {g}</div>
                </div>
              );
            })}
          </div>

          {/* Summary chips */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> {summary.total_items} líneas recibidas
            </span>
            <span>·</span>
            <span>{formatCurrency(summary.total_spend)} de gasto</span>
          </div>

          {/* Ranking list */}
          <div className="space-y-2 mt-2">
            {items.map((it) => {
              const style = GRADE_STYLE[it.grade] || GRADE_STYLE.C;
              const { Icon } = style;
              const incidentPct = Math.round((it.incident_rate || 0) * 100);
              const completenessPct = Math.round((it.completeness_rate || 0) * 100);
              return (
                <div
                  key={it.supplier_id}
                  className="border rounded-lg p-3 flex items-center gap-3"
                  data-testid={`scorecard-row-${it.supplier_id}`}
                >
                  <div
                    className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center shrink-0 border ${style.badge}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] font-bold mt-0.5">{it.grade}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{it.supplier_name}</p>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        {it.score}/100
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>
                        {it.orders_received} pedido{it.orders_received === 1 ? "" : "s"}
                      </span>
                      <span>· {it.items_with_incidents}/{it.items_received} líneas con incidencia</span>
                      <span>· {completenessPct}% completos</span>
                      {it.total_spend > 0 && <span>· {formatCurrency(it.total_spend)} gasto</span>}
                    </div>
                    {(it.by_type.missing > 0 || it.by_type.damaged > 0 || it.by_type.wrong_product > 0) && (
                      <div className="text-[11px] mt-1 flex flex-wrap gap-2">
                        {it.by_type.missing > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <AlertTriangle className="w-3 h-3" /> {it.by_type.missing} faltante(s)
                          </span>
                        )}
                        {it.by_type.damaged > 0 && (
                          <span className="inline-flex items-center gap-1 text-red-700">
                            <ShieldAlert className="w-3 h-3" /> {it.by_type.damaged} dañado(s)
                          </span>
                        )}
                        {it.by_type.wrong_product > 0 && (
                          <span className="inline-flex items-center gap-1 text-rose-700">
                            <Trophy className="w-3 h-3" /> {it.by_type.wrong_product} incorrecto(s)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-[11px] text-muted-foreground border-t pt-2 mt-2">
            Datos basados en eventos <code>purchase.order.received</code> de los últimos {days} días.
            Las notas se actualizan automáticamente al recibir nuevos pedidos.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
